import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./Welcome.module.scss";

import mrImg from "@/assets/UI/memberCreate/q1Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q1MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q1Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q1MrsM.webp";

const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 1, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 1, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

export default function Welcome() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [choice, setChoice] = useState("");

  const t = (k, o) => i18n.t(k, o);
  const lang = useMemo(() => (i18n.language || "en").slice(0, 2), []);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const title = t("onboarding.welcome.title", { defaultValue: "Welcome" });
  const subtitle = t("onboarding.welcome.q1", {
    defaultValue: "Tell us your experience level.",
  });

  const cta = t("common.continue", { defaultValue: "Continue" });

  const options = [
    {
      value: "experienced",
      emoji: "🔥",
      title: t("onboarding.exp.experienced.title", {
        defaultValue: "Experienced",
      }),
      hint: t("onboarding.exp.experienced.hint", {
        defaultValue: "You know the vibe and want to move fast.",
      }),
    },
    {
      value: "curious",
      emoji: "👀",
      title: t("onboarding.exp.curious.title", { defaultValue: "Curious" }),
      hint: t("onboarding.exp.curious.hint", {
        defaultValue: "You’re exploring and want guidance.",
      }),
    },
    {
      value: "first_time",
      emoji: "🌶️",
      title: t("onboarding.exp.first_time.title", {
        defaultValue: "First Time",
      }),
      hint: t("onboarding.exp.first_time.hint", {
        defaultValue: "You’re new and want the safest path.",
      }),
    },
  ];

  async function onSubmit(e) {
    e.preventDefault();

    if (!choice) {
      toast.info(
        t("onboarding.welcome.pickOne", { defaultValue: "Pick one option." }),
      );
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/v1/member-create",
        { experienceLevel: choice, lang },
        { skipToast: true },
      );
      navigate(data?.next || "/member/local", { replace: true });
    } catch (err) {
      console.error("create member failed:", err);
      toast.error(
        t("onboarding.welcome.error", {
          defaultValue: "Something went wrong.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={cls.page}>
      {/* ✅ Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* ✅ Scrolling content (background stays fixed) */}
      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.formCard}>
            <div className={cls.formTop}>
              <div className={cls.formMeta}>
                <div className={cls.formTitle}>{title}</div>
                <div className={cls.formSub}>{subtitle}</div>
              </div>
            </div>

            <form className={cls.form} onSubmit={onSubmit} noValidate>
              <fieldset className={cls.options}>
                <legend className="sr-only">{subtitle}</legend>

                {options.map((opt) => {
                  const checked = choice === opt.value;
                  return (
                    <label
                      key={opt.value}
                      className={`${cls.option} ${
                        checked ? cls.optionActive : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="experienceLevel"
                        value={opt.value}
                        checked={checked}
                        onChange={() => setChoice(opt.value)}
                      />

                      <span className={cls.optionBody}>
                        <span className={cls.optionEmoji} aria-hidden="true">
                          {opt.emoji}
                        </span>

                        <span className={cls.optionText}>
                          <strong>{opt.title}</strong>
                          <small>{opt.hint}</small>
                        </span>

                        <span className={cls.optionDot} aria-hidden="true" />
                      </span>
                    </label>
                  );
                })}
              </fieldset>

              {choice && (
                <div className={cls.aiWrap} role="status" aria-live="polite">
                  <div className={`${cls.aiNote} ${cls[`ai-${choice}`]}`}>
                    <span className={cls.aiPulse} aria-hidden="true" />
                    <p className={cls.aiText}>
                      {t(`onboarding.exp.${choice}.ai`, { defaultValue: "" })
                        .split("\n")
                        .filter(Boolean)
                        .map((line, idx) => (
                          <span key={idx}>
                            {line}
                            <br />
                          </span>
                        ))}
                    </p>
                  </div>
                </div>
              )}

              {/* ✅ Always visible submit */}
              <div className={cls.actions}>
                <button
                  type="submit"
                  className={cls.submit}
                  disabled={submitting || !choice}
                  aria-busy={submitting ? "true" : "false"}
                >
                  {submitting
                    ? t("common.saving", { defaultValue: "Saving…" })
                    : cta}
                </button>
              </div>
            </form>
          </div>

          {/* extra breathing room so page scroll feels nice */}
          <div className={cls.bottomSpace} />
        </div>
      </div>
    </main>
  );
}
